import { WEAPON_ICON_MAP } from "./constants";

/**
 * Get weapon icon URL from weapon name
 */
export const getWeaponIconUrl = (weapon: string): string => {
  if (!weapon) return "";
  // Normalize: lowercase and trim
  const normalized = weapon.toLowerCase().trim();
  // Look up in map, fallback to normalized name with spaces removed
  const iconName =
    WEAPON_ICON_MAP[normalized] || normalized.replace(/[\s-]/g, "");
  return `https://media.allstar.gg/static/web/cs2_icons/equipment/${iconName}.svg`;
};

/**
 * Convert tick to time string (M:SS format)
 */
export const tickToTimeString = (tick: number, tickRate: number): string => {
  const seconds = tick / tickRate;
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Parse time string (M:SS format) to tick
 */
export const timeStringToTick = (
  timeStr: string,
  tickRate: number,
): number | null => {
  const match = timeStr.match(/^(\d+):(\d{2})$/);
  if (!match) return null;
  const minutes = parseInt(match[1], 10);
  const seconds = parseInt(match[2], 10);
  if (seconds >= 60) return null;
  return (minutes * 60 + seconds) * tickRate;
};

/**
 * Generate a unique ID
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if a team string represents CT
 */
export const isCTTeam = (team: string): boolean => {
  return (
    team === "CounterTerrorists" ||
    team === "CT" ||
    team?.toLowerCase().includes("counter")
  );
};

/**
 * Clean player name by removing special prefixes
 */
export const cleanPlayerName = (name: string): string => {
  return name.replace(/^[⑧⑨⑩⑪⑫⑬]\s*[✓★☆]*\s*/, "").trim();
};

/**
 * Scoring constants for moment detection
 */
const MOMENT_SCORES = {
  ace: 100,
  "quad-kill": 80,
  "triple-kill": 60,
  "double-kill": 35,
  headshot_bonus: 5,
} as const;

/**
 * Labels for multi-kill types
 */
const MULTI_KILL_LABELS: Record<
  number,
  { type: string; label: string; icon: string }
> = {
  2: { type: "double-kill", label: "2K", icon: "🔥" },
  3: { type: "triple-kill", label: "3K", icon: "🔥" },
  4: { type: "quad-kill", label: "4K", icon: "🔥" },
  5: { type: "ace", label: "ACE", icon: "🏆" },
};

import {
  IRoundData,
  IKillEvent,
  IPlayer,
  ISuggestedMoment,
  TSuggestedMomentType,
} from "./types";

/**
 * Detect multi-kills and aces from match data
 * Multi-kill: 2+ kills by the same player within a time window
 * Ace: 5 kills by one player in a single round
 */
export const detectSuggestedMoments = (
  rounds: IRoundData[],
  players: IPlayer[],
  tickRate: number,
): ISuggestedMoment[] => {
  const moments: ISuggestedMoment[] = [];
  const MULTI_KILL_WINDOW = 30 * tickRate; // 30 seconds between kills

  // Create a player lookup map
  const playerMap = new Map<string, IPlayer>();
  players.forEach((p) => playerMap.set(p.steamId, p));

  for (const round of rounds) {
    // Skip invalid rounds
    if (round.round === 999 || !round.kills || round.kills.length === 0) {
      continue;
    }

    // Group kills by player (skip world/suicide kills and invalid players)
    const killsByPlayer = new Map<string, IKillEvent[]>();
    round.kills.forEach((kill) => {
      // Skip if no killer, killer is "0" (world), or killer not in player list
      if (!kill.killer || kill.killer === "0" || !playerMap.has(kill.killer))
        return;
      const existing = killsByPlayer.get(kill.killer) || [];
      killsByPlayer.set(kill.killer, [...existing, kill]);
    });

    killsByPlayer.forEach((kills, playerId) => {
      const player = playerMap.get(playerId);
      const playerName = player
        ? cleanPlayerName(player.name)
        : playerId.slice(-6);

      // Sort kills by tick
      const sortedKills = [...kills].sort((a, b) => a.tick - b.tick);

      // Check for ACE first (5 kills in a round - supersedes all other multi-kills)
      if (sortedKills.length >= 5) {
        const aceKills = sortedKills.slice(0, 5);
        const headshotCount = aceKills.filter(
          (k) => k.killerData?.headShot,
        ).length;

        moments.push({
          id: generateId(),
          type: "ace",
          score:
            MOMENT_SCORES.ace + headshotCount * MOMENT_SCORES.headshot_bonus,
          label: "ACE",
          icon: "🏆",
          startTick: aceKills[0].tick,
          endTick: aceKills[4].tick,
          playerId,
          playerName,
          round: round.round,
          kills: aceKills,
          headshotCount,
        });
        return; // Don't add multi-kills if we found an ace
      }

      // For 3+ kills in a round, always count as a multi-kill (impressive round performance)
      // For 2 kills, require them to be within the time window (rapid double kill)
      if (sortedKills.length >= 3) {
        // 3K or 4K in a round - use all kills regardless of timing
        const count = Math.min(sortedKills.length, 4);
        const multiKillKills = sortedKills.slice(0, count);
        const config = MULTI_KILL_LABELS[count];

        if (config) {
          const headshotCount = multiKillKills.filter(
            (k) => k.killerData?.headShot,
          ).length;

          const scoreKey = config.type as keyof typeof MOMENT_SCORES;

          moments.push({
            id: generateId(),
            type: config.type as TSuggestedMomentType,
            score:
              MOMENT_SCORES[scoreKey] +
              headshotCount * MOMENT_SCORES.headshot_bonus,
            label: config.label,
            icon: config.icon,
            startTick: multiKillKills[0].tick,
            endTick: multiKillKills[multiKillKills.length - 1].tick,
            playerId,
            playerName,
            round: round.round,
            kills: multiKillKills,
            headshotCount,
          });
        }
      } else if (sortedKills.length === 2) {
        // For double kills, require them to be within the time window
        if (sortedKills[1].tick - sortedKills[0].tick <= MULTI_KILL_WINDOW) {
          const config = MULTI_KILL_LABELS[2];
          const headshotCount = sortedKills.filter(
            (k) => k.killerData?.headShot,
          ).length;

          const scoreKey = config.type as keyof typeof MOMENT_SCORES;

          moments.push({
            id: generateId(),
            type: config.type as TSuggestedMomentType,
            score:
              MOMENT_SCORES[scoreKey] +
              headshotCount * MOMENT_SCORES.headshot_bonus,
            label: config.label,
            icon: config.icon,
            startTick: sortedKills[0].tick,
            endTick: sortedKills[1].tick,
            playerId,
            playerName,
            round: round.round,
            kills: sortedKills,
            headshotCount,
          });
        }
      }
    });
  }

  // Sort by score descending (best moments first)
  return moments.sort((a, b) => b.score - a.score);
};

/**
 * Filter suggested moments by player
 */
export const filterMomentsByPlayer = (
  moments: ISuggestedMoment[],
  playerId: string,
): ISuggestedMoment[] => {
  return moments.filter((m) => m.playerId === playerId);
};

/**
 * Get moment type display color
 */
export const getMomentTypeColor = (type: TSuggestedMomentType): string => {
  switch (type) {
    case "ace":
      return "#ffd700"; // Gold
    case "quad-kill":
      return "#ff6b6b"; // Red
    case "triple-kill":
      return "#71aaff"; // Blue
    case "double-kill":
      return "#4dbd94"; // Green
    default:
      return "#8fa9af"; // Gray
  }
};
